import { useEffect, useRef, useState } from 'react';
import type { Directory, VoicemailMessage } from '@stitch/pbx-adapter';
import { lookup } from '@stitch/pbx-adapter/describe';
import { api, type VoicemailView } from '../api/client';
import { fullWhen, shownNumber } from '../lib/format';
import { Icon } from '../lib/icons';
import { useData } from '../lib/useData';
import { heardVoicemail, voicemailListed } from '../lib/voicemail';
import { TranscriptSwitch } from './TranscriptSwitch';
import { Button, Chip, Dialog, Empty, LoadError, Loading, Notice } from './ui';

/**
 * A mailbox: newest first, play right here (playing marks it heard), add a note, move it to
 * someone else's box with a note, or delete it. Used on My phone and when an admin looks at
 * someone's (anything done to another person's box is written to the audit log).
 */
export function VoicemailBox({ domain, extension, dir, own }: { domain: string; extension?: string; dir: Directory | undefined; own: boolean }) {
  // A page of 50 at a time; "Show more" loads the next (and keeps them after a change).
  const [pages, setPages] = useState(1);
  const box = useData(async () => {
    const readAt = Date.now();
    const all = await Promise.all(Array.from({ length: pages }, (_, i) => api.voicemail(domain, extension, i + 1)));
    const first = all[0]!;
    const seen = new Set<string>();
    const messages = all.flatMap((p) => p.messages).filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true)));
    const merged = { ...first, messages, more: Boolean(all[all.length - 1]?.more) };
    // Your own: a notice for a message heard or deleted on a desk phone goes, and the count follows.
    if (own) voicemailListed(domain, merged, readAt);
    return merged;
  }, [domain, extension, pages]);
  const [playing, setPlaying] = useState('');
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState('');
  const [noteFor, setNoteFor] = useState<VoicemailMessage | null>(null);
  const [moveFor, setMoveFor] = useState<VoicemailMessage | null>(null);
  const [removeFor, setRemoveFor] = useState<VoicemailMessage | null>(null);

  // Transcripts being written: look again every few seconds, for up to two minutes for each new
  // set of them (new voicemail, another box or page starts the count again).
  const pending = box.data?.messages.filter((m) => m.transcript?.status === 'pending').map((m) => m.id).join(',') ?? '';
  const looks = useRef({ key: '', n: 0 });
  const { reload } = box;
  useEffect(() => {
    if (!pending) return;
    const key = `${domain}|${extension}|${pending}`;
    if (looks.current.key !== key) looks.current = { key, n: 0 };
    if (looks.current.n >= 30) return;
    const t = window.setTimeout(() => {
      looks.current.n++;
      reload();
    }, 4000);
    return () => window.clearTimeout(t);
  }, [pending, box.data, reload, domain, extension]);

  if (box.error) return <LoadError error={box.error} retry={box.reload} />;
  if (!box.data) return <Loading label="Loading voicemail" />;
  const v = box.data;
  const who = (m: VoicemailMessage) => {
    const known = m.from.number ? lookup(dir, m.from.number) : undefined;
    const shown = m.from.number ? shownNumber(m.from.number) : '';
    const name = m.from.name || (known && known.kind !== 'external' ? known.name : '') || shown || 'Unknown caller';
    return { name, number: shown && shown !== name ? shown : '' };
  };

  async function act(id: string, fn: () => Promise<unknown>) {
    setBusy(id);
    setProblem('');
    try {
      await fn();
      box.reload();
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy('');
    }
  }

  function play(m: VoicemailMessage) {
    setPlaying(m.id);
    if (!m.isNew) return;
    // Your own: its new-voicemail notice goes, and the Calls badge drops.
    if (own) heardVoicemail(domain, m.id);
    void api.changeVoicemail(domain, m.id, { heard: true }, extension).then(box.reload, () => undefined);
  }

  const people = (dir?.entries ?? []).filter((e) => e.kind === 'person' && e.extension !== v.extension);

  return (
    <div className="voicemail">
      {problem && <Notice tone="crit">{problem}</Notice>}
      {v.transcripts && <TranscriptSwitch domain={domain} mailbox={v.extension} own={own} whose="this box’s" state={v.transcripts} onChanged={box.reload} />}
      {v.messages.length === 0 ? (
        <Empty title={own ? 'No voicemail' : 'No voicemail in this box'}>{own ? 'New messages show up here.' : undefined}</Empty>
      ) : (
        <ul className="vm-list">
          {v.messages.map((m) => {
            const p = who(m);
            return (
              <li key={m.id} className={m.isNew ? 'new' : ''}>
                <div className="vm-row">
                  <button type="button" className="icon-btn vm-play" aria-label={`Play the message from ${p.name}`} onClick={() => play(m)} disabled={!m.hasAudio}>
                    <Icon name="play" size={16} />
                  </button>
                  <span className="cell-stack grow">
                    <strong>
                      {p.name} {m.isNew && <Chip small tone="brand">New</Chip>}
                    </strong>
                    <small className="muted">
                      {p.number ? `${p.number} · ` : ''}
                      {fullWhen(m.time)} · {m.seconds}s{m.leftAt ? ` · left at ${lookup(dir, m.leftAt).name || m.leftAt}` : ''}
                    </small>
                    {m.note && <small className="vm-note">“{m.note}”</small>}
                    <Transcript t={(m as VoicemailView).transcript} />
                  </span>
                  {v.canChange && (
                    <span className="row-actions">
                      <button type="button" className="icon-btn" aria-label={`Add a note to the message from ${p.name}`} title="Note" onClick={() => setNoteFor(m)} disabled={busy === m.id}>
                        <Icon name="edit" size={16} />
                      </button>
                      <button type="button" className="icon-btn" aria-label={`Move the message from ${p.name}`} title="Move to someone else" onClick={() => setMoveFor(m)} disabled={busy === m.id}>
                        <Icon name="arrowOut" size={16} />
                      </button>
                      <button type="button" className="icon-btn" aria-label={`Delete the message from ${p.name}`} title="Delete" onClick={() => setRemoveFor(m)} disabled={busy === m.id}>
                        <Icon name="trash" size={16} />
                      </button>
                    </span>
                  )}
                </div>
                {playing === m.id && (
                  // Played from Stitch, which fetches it from the phone system for this mailbox only.
                  <audio className="vm-audio" controls autoPlay src={api.voicemailAudioUrl(domain, m.id, extension)} aria-label={`Message from ${p.name}`} />
                )}
              </li>
            );
          })}
        </ul>
      )}
      {v.more && (
        <div className="vm-more">
          <Button variant="secondary" size="small" disabled={box.loading} onClick={() => setPages((n) => n + 1)}>
            {box.loading ? 'Loading…' : `Show more (${v.total - v.messages.length} older)`}
          </Button>
        </div>
      )}
      {!own && <p className="muted small">Listening to, moving or deleting someone else’s voicemail is recorded in the activity log.</p>}

      <NoteDialog
        message={noteFor}
        onClose={() => setNoteFor(null)}
        onSave={(note) => {
          const m = noteFor!;
          setNoteFor(null);
          void act(m.id, () => api.changeVoicemail(domain, m.id, { note }, extension));
        }}
      />
      <MoveDialog
        message={moveFor}
        people={people.map((e) => ({ extension: e.extension, name: e.name || e.extension }))}
        onClose={() => setMoveFor(null)}
        onMove={(to, note) => {
          const m = moveFor!;
          setMoveFor(null);
          void act(m.id, () => api.changeVoicemail(domain, m.id, { moveTo: to, note }, extension));
        }}
      />
      <Dialog
        open={removeFor !== null}
        onClose={() => setRemoveFor(null)}
        title="Delete this voicemail?"
        subtitle={removeFor ? `From ${who(removeFor).name}, ${fullWhen(removeFor.time)}. This can’t be undone.` : undefined}
        foot={
          <>
            <Button variant="ghost" onClick={() => setRemoveFor(null)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const m = removeFor!;
                setRemoveFor(null);
                void act(m.id, () => api.deleteVoicemail(domain, m.id, extension));
              }}
            >
              Delete
            </Button>
          </>
        }
      >
        <p className="muted">Deleted messages can’t be brought back.</p>
      </Dialog>
    </div>
  );
}

function NoteDialog({ message, onClose, onSave }: { message: VoicemailMessage | null; onClose: () => void; onSave: (note: string) => void }) {
  const [note, setNote] = useState('');
  const [for_, setFor] = useState('');
  if (message && for_ !== message.id) {
    setFor(message.id);
    setNote(message.note);
  }
  return (
    <Dialog
      open={message !== null}
      onClose={onClose}
      title="Note on this voicemail"
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => onSave(note.trim())}>
            Save note
          </Button>
        </>
      }
    >
      <label className="field">
        <span className="field-label">Note</span>
        <textarea className="input" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Called back, left a message" autoFocus />
      </label>
    </Dialog>
  );
}

function MoveDialog({
  message,
  people,
  onClose,
  onMove
}: {
  message: VoicemailMessage | null;
  people: { extension: string; name: string }[];
  onClose: () => void;
  onMove: (to: string, note: string) => void;
}) {
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  return (
    <Dialog
      open={message !== null}
      onClose={onClose}
      title="Move this voicemail"
      subtitle="It goes to their voicemail as a new message, with your note."
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!to} onClick={() => (onMove(to, note.trim()), setTo(''), setNote(''))}>
            Move it
          </Button>
        </>
      }
    >
      <label className="field">
        <span className="field-label">To</span>
        <select className="input" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Move to">
          <option value="">Choose someone</option>
          {people.map((p) => (
            <option key={p.extension} value={p.extension}>
              {p.name} ({p.extension})
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span className="field-label">Note (optional)</span>
        <textarea className="input" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Can you call them back?" />
      </label>
    </Dialog>
  );
}

/** What the caller said, as Stitch transcribed it (long ones fold). */
function Transcript({ t }: { t: VoicemailView['transcript'] }) {
  const [open, setOpen] = useState(false);
  if (!t) return null;
  if (t.status === 'pending') return <small className="vm-transcript muted">Writing out the message…</small>;
  if (t.status === 'failed') return <small className="vm-transcript muted">The message couldn’t be written out. Play it instead.</small>;
  const text = t.text?.trim() ?? '';
  if (!text) return <small className="vm-transcript muted">No words heard.</small>;
  const long = text.length > 220;
  return (
    <span className="vm-transcript">
      <span>{long && !open ? `${text.slice(0, 200).replace(/\s+\S*$/, '')}…` : text}</span>
      {long && (
        <button type="button" className="link-btn small-print" onClick={() => setOpen((o) => !o)}>
          {open ? 'Less' : 'More'}
        </button>
      )}
    </span>
  );
}
